import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { ChangeRequest, ChangeRequestStatus, ChangeRequestPriority } from '@/types/changeRequest';
import { useToast } from '@/hooks/use-toast';

interface SubmitChangeRequestData {
  title: string;
  description: string;
  use_case?: string;
  page_url?: string;
  attachments?: string[];
}

export function useChangeRequests() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch all change requests (admin only - RLS handles this)
  const { data: changeRequests = [], isLoading } = useQuery({
    queryKey: ['changeRequests'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('change_requests')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as ChangeRequest[];
    },
    enabled: !!user?.id,
  });

  // Submit a new change request
  const submitRequest = useMutation({
    mutationFn: async (data: SubmitChangeRequestData) => {
      if (!user?.id) throw new Error('Must be logged in');
      
      const { error } = await supabase
        .from('change_requests')
        .insert({
          user_id: user.id,
          title: data.title,
          description: data.description,
          use_case: data.use_case || null,
          page_url: data.page_url || null,
          attachments: data.attachments || [],
        });
      
      if (error) throw error;
    },
    onSuccess: () => {
      toast({
        title: 'Change request submitted',
        description: 'Thank you for your suggestion! We\'ll review it.',
      });
      queryClient.invalidateQueries({ queryKey: ['changeRequests'] });
    },
    onError: (error) => {
      console.error('Error submitting change request:', error);
      toast({
        title: 'Error',
        description: 'Failed to submit change request. Please try again.',
        variant: 'destructive',
      });
    },
  });

  // Update change request status (admin only)
  const updateStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: ChangeRequestStatus }) => {
      const updates: Partial<ChangeRequest> = { status };
      if (status === 'completed' || status === 'declined') {
        updates.resolved_at = new Date().toISOString();
      } else {
        updates.resolved_at = null;
      }
      
      const { error } = await supabase
        .from('change_requests')
        .update(updates)
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['changeRequests'] });
      toast({ title: 'Status updated' });
    },
  });

  // Update change request priority (admin only)
  const updatePriority = useMutation({
    mutationFn: async ({ id, priority }: { id: string; priority: ChangeRequestPriority }) => {
      const { error } = await supabase
        .from('change_requests')
        .update({ priority })
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['changeRequests'] });
      toast({ title: 'Priority updated' });
    },
  });

  // Update admin notes (admin only)
  const updateNotes = useMutation({
    mutationFn: async ({ id, admin_notes }: { id: string; admin_notes: string }) => {
      const { error } = await supabase
        .from('change_requests')
        .update({ admin_notes })
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['changeRequests'] });
      toast({ title: 'Notes saved' });
    },
  });

  // Delete change request (admin only)
  const deleteRequest = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('change_requests')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['changeRequests'] });
      toast({ title: 'Change request deleted' });
    },
  });

  const newCount = changeRequests.filter(r => r.status === 'new').length;

  return {
    changeRequests,
    isLoading,
    newCount,
    submitRequest,
    updateStatus,
    updatePriority,
    updateNotes,
    deleteRequest,
  };
}
